import { useEffect, useState } from "react";
import cx from "classnames";
import _ from "lodash";

import { DealRoomActionsProgress } from "src/types/dealRoom";

import styles from "./styles.module.scss";

interface SummaryProps {
  summaryDetails: DealRoomActionsProgress | null;
}

enum SummaryCardTypes {
  Completed = "completedActions",
  Pending = "pendingActions",
  Overdue = "overdueActions",
}

const SUMMARY_CARD_TYPES = [
  SummaryCardTypes.Completed,
  SummaryCardTypes.Pending,
  SummaryCardTypes.Overdue,
];

interface SummaryCardProperty {
  id: SummaryCardTypes;
  icon: string;
  text: string;
}

const summaryCardsProperties: SummaryCardProperty[] = [
  {
    id: SummaryCardTypes.Completed,
    icon: "paperflite-tick-unselected",
    text: "completed",
  },
  {
    id: SummaryCardTypes.Pending,
    icon: "paperflite-pending-actions",
    text: "pending",
  },
  {
    id: SummaryCardTypes.Overdue,
    icon: "paperflite-overdue-actions",
    text: "overdue",
  },
];

const getSummaryCardProperties = (type: SummaryCardTypes) => {
  const targetSummaryCardProperties = _.find(
    summaryCardsProperties,
    (summaryCard) => summaryCard.id === type,
  );

  if (!targetSummaryCardProperties) {
    return {
      id: SummaryCardTypes.Completed,
      icon: "",
      text: "",
      style: "",
    };
  }

  return targetSummaryCardProperties;
};

interface SummaryCardProps {
  count: number;
  type: SummaryCardTypes;
}

const SummaryCard = ({ count, type }: SummaryCardProps) => {
  const { id, icon, text } = getSummaryCardProperties(type);

  if (_.size(text) < 1) {
    return <></>;
  }

  return (
    <div className={styles.summaryCard}>
      <i
        className={cx(styles.icon, icon, {
          [styles.completed]: id === SummaryCardTypes.Completed,
          [styles.overdue]: id === SummaryCardTypes.Overdue,
          [styles.pending]: id === SummaryCardTypes.Pending,
        })}
      />
      <p className={styles.text}>
        <span>{count}</span>
        {` ${text}`}
      </p>
    </div>
  );
};

const SummaryCards = ({ summaryDetails }: SummaryProps) => {
  return (
    <div className={styles.summaryCardsContainer}>
      {SUMMARY_CARD_TYPES.map((summaryCardType, index) => {
        const targetSummaryDetail = _.get(summaryDetails, `[${summaryCardType}]`, 0);

        return (
          <SummaryCard
            key={`summaryCard-${index}`}
            count={targetSummaryDetail}
            type={summaryCardType}
          />
        );
      })}
    </div>
  );
};

interface PercentageContainerProps {
  percentage: number;
}

const PercentageContainer = ({ percentage }: PercentageContainerProps) => {
  const [animatedValue, setAnimatedValue] = useState(0);
  useEffect(() => {
    setAnimatedValue(0);

    const animationDuration = 2000;
    const steps = Math.floor(percentage);
    const stepDuration = animationDuration / steps;
    const increment = 1;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const newValue = Math.min(increment * step, percentage);
      setAnimatedValue(newValue);

      if (step >= steps) {
        clearInterval(timer);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [percentage]);

  return (
    <div className={styles.percentage}>
      <div className={styles.progressBarContainer}>
        <div className={styles.progressBarUnfilled}></div>
        <div
          className={styles.progressBarFilled}
          style={{ width: `${animatedValue}%` }}
        ></div>
      </div>
      <p className={styles.value}>{`${animatedValue} %`}</p>
    </div>
  );
};

const Summary = ({ summaryDetails }: SummaryProps) => {
  if (!summaryDetails) {
    return <></>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.summaryContainer}>
        <p className={styles.text}>
          Total Action Items : <span>{summaryDetails.totalActions}</span>
        </p>

        <SummaryCards summaryDetails={summaryDetails} />
      </div>

      <PercentageContainer
        percentage={_.get(summaryDetails, "percentCompleted", 0)}
      />
    </div>
  );
};

export default Summary;
